import { FolderOpen, Plus } from 'lucide-react'
import { useState } from 'react'

import { useProjects, useUpdateParcel } from '../api/client'
import { de } from '../i18n/de'

/**
 * Project assignment, inline on the row.
 *
 * Deliberately a native <select>: it is keyboard accessible for free, works on touch, and
 * the list is short enough that a custom combobox would add risk without adding value.
 */
export function ProjectChip({
  parcelId,
  projectId,
  projectName,
}: {
  parcelId: number
  projectId: number | null
  projectName: string | null
}) {
  const { data: projects } = useProjects()
  const updateParcel = useUpdateParcel()
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <select
        autoFocus
        value={projectId ?? ''}
        disabled={updateParcel.isPending}
        onClick={(event) => event.stopPropagation()}
        onBlur={() => setEditing(false)}
        onChange={(event) => {
          const value = event.target.value
          updateParcel.mutate({
            id: parcelId,
            patch: { project_id: value === '' ? null : Number(value) },
          })
          setEditing(false)
        }}
        className="max-w-[11rem] rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-800"
      >
        <option value="">{de.parcel.noProject}</option>
        {projects?.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
    )
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        setEditing(true)
      }}
      title={de.parcel.project}
      className={`relative after:absolute after:-inset-1.5 after:content-[''] inline-flex max-w-[11rem] items-center gap-1 rounded-full px-2 py-0.5 text-xs transition ${
        projectName
          ? 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-950 dark:text-indigo-200 dark:hover:bg-indigo-900'
          : 'border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600 dark:border-slate-600 dark:text-slate-400 dark:hover:text-slate-300'
      }`}
    >
      {projectName ? (
        <>
          <FolderOpen className="h-3 w-3 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{projectName}</span>
        </>
      ) : (
        <>
          <Plus className="h-3 w-3 shrink-0" aria-hidden />
          <span>{de.parcel.project}</span>
        </>
      )}
    </button>
  )
}
